import { DefaultState } from 'features/types';
import { SelectItem } from 'api/types';
import { JobResponseTypes, JobStatuses } from 'features/jobs/types';
import { EmployerSelectItem } from 'features/job-seekers/types';
import { FormikProps, FormikErrors } from 'formik';

export type JobFormState = DefaultState & {
  data: {
    employerFormData: JobEmployerFormData | null;
    jobSeekerFormData: JobJobSeekerFormData | null;
    organizationFormData: JobOrganizationFormData | null;
  }
};

export type JobFormEditBaseData = {
  code?: string;
  title?: string;
  description?: string;
  language?: Languages;
  employerLogo: string;
  languageProficiency?: string;
  locationOption?: JobLocations;
  location?: google.maps.LatLngLiteral | undefined;
  fullAddress?: string;
  radius?: string;
  travelLocationAreas?: string;
  overseasLocationAreas?: string;
  jobTypes?: string[];
  payRangeMin?: string;
  payRangeMax?: string;
  payrate?: string;
  jobExpiration?: string;
  supplementalPayOptions?: string[];
  supplementalPayOther?: string;
  benefits?: string[];
  benefitsOther?: string;
  skills?: string[];
  certifications?: string[];
  niceToHaveAttributes?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactCalendarLink?: string;
  transportationToJobSite?: string;
  backgroundCheckGuidelines?: string;
  status?: JobStatuses;
  applyLink?: string;
  responseType?: JobResponseTypes;
  directResponseRecipients?: string[];
  publishType: JobPublishTypes;
  programs?: SelectItem[];
  experienceLevels?: string[];
  isPublicOnPcs?: boolean;
};

type JobEmployerFormEditData = JobFormEditBaseData & {
  employerName?: string;
};

type JobOrganizationFormEditData = JobFormEditBaseData & {
  employerName?: string;
};

export type JobFormSelectLists = {
  languagesSelectList: SelectItem[];
  languageProficienciesSelectList: SelectItem[];
  jobLocationsSelectList: SelectItem[];
  jobTypesSelectList: SelectItem[];
  payrateOptionsSelectList: SelectItem[];
  jobExpirationSelectList: SelectItem[];
  supplementalPayOptionsSelectList: SelectItem[];
  benefitsSelectList: SelectItem[];
  transportationToJobSiteSelectList: SelectItem[];
  jobResponseTypesSelectList: SelectItem[];
  experienceLevelsSelectList: SelectItem[];
  savedEmployersSelectList: EmployerSelectItem[];
};

export type JobEmployerFormData = JobEmployerFormEditData & JobFormSelectLists & {
  limitedNumberOfPrograms: boolean;
};
export type JobJobSeekerFormData = JobEmployerFormEditData & JobFormSelectLists & {
  limitedNumberOfPrograms: boolean;
  networks?: SelectItem[];
  postedByAdminCommunityId?: string;
};
export type JobOrganizationFormData = JobOrganizationFormEditData & JobFormSelectLists;

export type JobForm = {
  title: string;
  code?: string;
  description?: string;
  language?: Languages;
  languageProficiency?: string;
  locationOption?: JobLocations;
  fullAddress?: string;
  radius?: string;
  travelLocationAreas?: string;
  overseasLocationAreas?: string;
  jobTypes?: string[];
  payRangeMin?: string;
  payRangeMax?: string;
  payrate?: string;
  jobExpiration?: string;
  supplementalPayOptions?: string[];
  supplementalPayOther?: string;
  benefits?: string[];
  benefitsOther?: string;
  skills: string[];
  certifications: string[];
  location?: google.maps.LatLngLiteral;
  niceToHaveAttributes?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactCalendarLink?: string;
  transportationToJobSite?: string;
  backgroundCheckGuidelines?: string;
  applyLink?: string;
  responseType: JobResponseTypes;
  directResponseRecipients?: string[];
  employerName?: string;
  employerLogo?: Blob;
  cloneLogoFromSavedEmployerId?: string;
  cloneLogoFromJobId?: string;
  removeEmployerLogo?: boolean;
  programsIds?: string[];
  programs?: SelectItem[];
  networksIds?: string[];
  networks?: SelectItem[];
  publishType: JobPublishTypes;
  experienceLevels?: string[];
  isPublicOnPcs?: boolean;
};

export type JobFormFieldsValidation = {
  isValid: boolean;
  hasOnlyProgramsError: boolean;
  validationErrors: FormikErrors<JobForm>;
  errorsCount: number;
}

export type JobFormRef = FormikProps<JobForm> & {
  mapJobData: (data: JobForm) => JobForm;
  validateJobFormFields: (forDraft: boolean, validateForm: Function, values: JobForm, setFieldError: (field: string, message: string | undefined) => void, setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void) => Promise<JobFormFieldsValidation>;
  validateSelectedPrograms: (programs: SelectItem[], jobTypes: string[]) => Promise<any>;
};

export enum JobPublishTypes {
  ToAll = 'ToAll',
  ToSpecific = 'ToSpecific',
}

export type SavedEmployerData = {
  employerName?: string;
  employerLogo?: Blob;
  cloneLogoFromSavedEmployerId?: string;
  cloneLogoFromJobId?: string;
  cloneLogoFromCurrentJobId?: string;
  removeEmployerLogo?: boolean;
  employerLogoUrl?: string;
}

export enum JobLocations {
  Onsite = 'Onsite',
  Remote = 'Remote',
  Multiple = 'Multiple',
  Travel = 'Travel',
  Overseas = 'Overseas',
}

export enum Languages {
  English = 'English',
  Spanish = 'Spanish',
  EnglishOrSpanish = 'EnglishOrSpanish',
  NotRequired = 'NotRequired',
}
